/**
 * Dark mode.
 *
 * Named useDarkMode, NOT useTheme — @bloomneo/uikit already exports a
 * `useTheme()` for its own theme/mode state and the two would be easy to
 * confuse at an import site.
 *
 * How it works: the user picks light / dark / system. This module resolves
 * "system" itself (via matchMedia) and always stamps a concrete
 * `data-theme="light|dark"` on <html>. styles/index.css keys the whole
 * `--fb-*` palette off that attribute, and AppRail's `C` object reads
 * those vars — so one attribute flip re-themes every surface that goes
 * through the palette.
 *
 * Resolving "system" here rather than with a CSS prefers-color-scheme
 * block keeps ONE source of truth for "which theme am I in". A media
 * query would fight the explicit attribute and force every token to be
 * declared twice.
 *
 * State lives in a module-level store, not in each hook instance, because
 * there are two independent consumers — the toggle in the app rail and
 * the <ThemeProvider mode> in main.tsx — and per-instance useState would
 * let them disagree (rail flips to dark, uikit stays light). useSync-
 * ExternalStore subscribes both to the same value.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';

export type ThemePref = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'fb-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(DARK_QUERY).matches;
}

function readStoredPref(): ThemePref {
  if (typeof window === 'undefined') return 'light';
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* private mode / storage disabled — fall through to the default */
  }
  return 'light';
}

export function resolveTheme(pref: ThemePref): ResolvedTheme {
  return pref === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : pref;
}

/** Stamp the resolved theme onto <html>, where index.css reads it. */
export function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = resolved;
}

// ── Module store ────────────────────────────────────────────────────────
let pref: ThemePref = readStoredPref();
let resolved: ResolvedTheme = resolveTheme(pref);
// One cached snapshot object per change — useSyncExternalStore compares by
// identity, so this must NOT be rebuilt on every getSnapshot call or React
// re-renders forever.
let snapshot = { pref, resolved };
const listeners = new Set<() => void>();

function emit() {
  snapshot = { pref, resolved };
  for (const l of listeners) l();
}

function commit(next: ThemePref, persist: boolean) {
  pref = next;
  resolved = resolveTheme(next);
  applyTheme(resolved);
  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage disabled — the choice just won't survive a reload */
    }
  }
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Apply the stored preference and start following the OS. Call once at app
 * boot (App.tsx), alongside useApplyBranding.
 */
export function initTheme(): () => void {
  applyTheme(resolved);
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mq = window.matchMedia(DARK_QUERY);
  const onChange = () => {
    // Only the "system" preference tracks the OS; an explicit light/dark
    // choice must survive the user changing their OS appearance.
    if (pref !== 'system') return;
    resolved = mq.matches ? 'dark' : 'light';
    applyTheme(resolved);
    emit();
  };
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

export function useDarkMode() {
  const snap = useSyncExternalStore(subscribe, () => snapshot, () => snapshot);
  const setPref = useCallback((next: ThemePref) => commit(next, true), []);
  /** Straight light↔dark flip. From "system" it moves to the opposite of
   *  whatever the OS is currently giving you, which is what a single
   *  toggle click is understood to mean. */
  const toggle = useCallback(() => {
    commit(resolveTheme(pref) === 'dark' ? 'light' : 'dark', true);
  }, []);

  return { pref: snap.pref, resolved: snap.resolved, isDark: snap.resolved === 'dark', setPref, toggle };
}

/**
 * Pin the document to LIGHT while the calling component is mounted, then
 * restore the user's preference on unmount.
 *
 * For surfaces that genuinely can't theme: the marketing/brochure pages
 * still carry their own light-only palettes, so a dark canvas gives them
 * dark text on a dark background. Rendering them light in dark mode is the
 * lesser evil, and it keeps shared chrome (the logo, the footer) consistent
 * with the page around it instead of a white wordmark on a white header.
 *
 * Remove the call once a surface's palette is converted.
 */
export function useForceLightTheme(): void {
  useEffect(() => {
    applyTheme('light');
    return () => { applyTheme(resolveTheme(pref)); };
  }, []);
}
